import type {
    HttpPort,
    IWorkflowService,
    Json,
    WorkflowGraph,
    WorkflowRequirementStatus,
    WorkflowTransition,
    WorkflowTransitionResult,
} from "../contracts";

// WorkflowService — wraps /api/workflow/*. The board-drag flow needs
// `transitionToStage`, which does the 2-step lookup: GET /available →
// find the transition whose to_stage matches the dropped column's value
// → POST /transition with that code.
export class WorkflowService implements IWorkflowService {
    constructor(private readonly http: HttpPort) {}

    async graph(modelKey: string, recordUuid: string): Promise<WorkflowGraph> {
        const qs = new URLSearchParams({ record_model: modelKey, record_uuid: recordUuid });
        return this.http.get<WorkflowGraph>(`/api/workflow/graph?${qs.toString()}`);
    }

    async available(modelKey: string, recordUuid: string): Promise<WorkflowTransition[]> {
        const qs = new URLSearchParams({ record_model: modelKey, record_uuid: recordUuid });
        const res = await this.http.get<{ transitions?: WorkflowTransition[] }>(
            `/api/workflow/available?${qs.toString()}`,
        );
        return res.transitions ?? [];
    }

    async transition(modelKey: string, recordUuid: string, code: string, ctx?: Json): Promise<WorkflowTransitionResult> {
        return this.http.post<WorkflowTransitionResult>("/api/workflow/transition", {
            record_model: modelKey,
            record_uuid: recordUuid,
            code,
            ctx: ctx ?? null,
        });
    }

    async requirements(modelKey: string, recordUuid: string): Promise<WorkflowRequirementStatus> {
        const qs = new URLSearchParams({ record_model: modelKey, record_uuid: recordUuid });
        return this.http.get<WorkflowRequirementStatus>(`/api/workflow/requirements?${qs.toString()}`);
    }

    async completeRequirement(requirementId: string, recordUuid: string): Promise<void> {
        await this.http.post("/api/workflow/requirement/complete", {
            requirement_id: requirementId,
            record_uuid: recordUuid,
        });
    }

    async reopenRequirement(requirementId: string, recordUuid: string): Promise<void> {
        await this.http.post("/api/workflow/requirement/reopen", {
            requirement_id: requirementId,
            record_uuid: recordUuid,
        });
    }

    async transitionToStage(modelKey: string, recordUuid: string, targetStage: Json): Promise<WorkflowTransitionResult> {
        const transitions = await this.available(modelKey, recordUuid);
        // Compare target_stage by stringified value — `to_stage` from the wire
        // is usually a UUID string, but enum-stage workflows use the code, and
        // we accept either shape on the drop side.
        const targetStr = targetStage == null ? "" : String(targetStage);
        const match = transitions.find((t) => String(t.to_stage ?? "") === targetStr);
        if (!match) {
            throw new Error(
                `No workflow transition available to target stage "${targetStr}". ` +
                    `Available transitions: ${transitions.map((t) => t.code).join(", ") || "(none)"}.`,
            );
        }
        return this.transition(modelKey, recordUuid, match.code);
    }
}
